import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, InboxArrowDownIcon } from '@heroicons/react/24/outline';
import { useDevicesToCollect, useCollectDevice } from '../hooks/useDevices';

// Logistics collection queue. Every in-service device held by someone currently
// on the offboarding register (open / in progress) — the hardware IT have
// flagged for return but that hasn't physically come back yet. Marking one
// collected returns it to spare stock and records the collection on the
// person's offboarding trail (ISO A.5.11, return of assets).

const LEAVER_STATUS = {
  open: { label: 'Open', className: 'bg-amber-100 text-amber-800 ring-amber-600/30' },
  in_progress: { label: 'In progress', className: 'bg-sky-100 text-sky-800 ring-sky-600/30' },
};
const SOURCE = { brighthr_terminated: 'Left BrightHR', leaver_check_mismatch: 'Leaver Check', manual: 'Manual' };

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function DevicesToCollect() {
  const { data, isLoading, error } = useDevicesToCollect();
  const collect = useCollectDevice();
  const [message, setMessage] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const devices = data?.devices || [];

  const doCollect = async (d) => {
    if (!window.confirm(`Mark ${d.serial_number || 'this device'} collected from ${d.assigned_name || 'the leaver'} and return it to stock?`)) return;
    setMessage(null);
    setBusyId(d.id);
    try {
      const r = await collect.mutateAsync(d.id);
      setMessage(`${r.serial || 'Device'} collected and returned to spare stock.${r.loggedToLeaver ? ' Recorded on the offboarding trail.' : ''}`);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not mark that collected.');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) return <div className="p-8"><p className="text-sm text-gray-400">Loading collection queue…</p></div>;
  if (error) {
    return (
      <div className="p-8">
        <p className="text-sm text-rose-700">
          {error.response?.status === 403 ? 'Devices to Collect is restricted to Logistics.' : 'Could not load the collection queue.'}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <Link to="/?dept=logistics" className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Logistics
      </Link>
      <div className="mb-4 flex items-center gap-3">
        <InboxArrowDownIcon className="h-7 w-7 text-brand" />
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Devices to Collect</h1>
          <p className="mt-1 text-sm text-gray-500">
            {devices.length === 0
              ? 'Nothing outstanding — every leaver has had their kit returned.'
              : `${devices.length} device${devices.length > 1 ? 's' : ''} still held by people being offboarded.`}
          </p>
        </div>
      </div>

      {message ? <p className="mb-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">{message}</p> : null}

      {devices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
          <InboxArrowDownIcon className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-2 text-sm text-gray-400">No devices waiting to be collected.</p>
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['Leaver', 'Device', 'Held since', 'Offboarding', 'Flagged', ''].map((h, i) => (
                  <th key={i} className="sticky top-0 z-10 whitespace-nowrap bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => {
                const st = LEAVER_STATUS[d.leaver_status] || { label: d.leaver_status, className: 'bg-gray-100 text-gray-600 ring-gray-500/30' };
                return (
                  <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{d.assigned_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">
                      <div className="font-medium tabular-nums text-gray-800">{d.serial_number || 'no serial'}</div>
                      <div className="text-xs text-gray-400">{[d.make_model, d.device_type].filter(Boolean).join(' · ') || d.hostname || ''}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-500">{fmtDate(d.assigned_at)}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${st.className}`}>{st.label}</span>
                      <span className="ml-2 text-xs text-gray-400">{SOURCE[d.leaver_source] || d.leaver_source}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-500">{fmtDate(d.detected_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => doCollect(d)} disabled={busyId === d.id}
                        className="rounded-md border border-green-300 px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-40">
                        {busyId === d.id ? 'Collecting…' : 'Mark collected'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-400">
        This list is driven by the offboarding register — a device appears here as soon as its holder is flagged as a leaver, and drops off once collected.
      </p>
    </div>
  );
}

export default DevicesToCollect;
